import { useAssetVizInternal } from 'features/assetViz/AssetVizInternalProvider'
import { HtmlOverlay3D } from 'features/assetViz/objects/decor/HtmlOverlay3D'
import { TankDecorGroup3D } from 'features/assetViz/objects/decor/TankDecorGroup3D'
import {
  SurfaceMeshFeature3D,
  SurfaceMeshSelectionType,
} from 'features/assetViz/objects/features/procedural/SurfaceMeshFeature3D'
import { HtmlOverlayPriority, Vec3 } from 'features/assetViz/utils/helpers'
import { useGeckoLocale } from 'features/locale'
import { sqrt } from 'mathjs'
import React from 'react'
import {
  GeckoComponentModelType,
  GeckoComponentPolygon,
  GeckoCone,
  GeckoConeCourse,
  GeckoLegacyCone,
} from 'types'

/**
 * A component used to represent a Cone course on a component's surface
 * @param component         The Cone component model
 * @param model             The Cone course model
 * @constructor
 */
export const ConeCourseFeature3D: React.FC<{
  component: GeckoLegacyCone | GeckoCone
  model: GeckoConeCourse
}> = ({ model, component }) => {
  const {
    showFeatures,
    showShellCourseDebugColors,
    clipConfig,
    show3D,
    showFeatureLabels,
  } = useAssetVizInternal()
  const { path, nominal, name, y_end } = React.useMemo(() => {
    const nominal = model.wallNominalThickness
    const name = model.name
    const path = GeckoComponentPolygon.CreateFromRect(model.geometry)
    return {
      path: path,
      nominal: nominal,
      name: name,
      y_end: model.geometry.end.y,
    }
  }, [model])

  const namePlatePosition = React.useMemo<Vec3>(() => {
    //Do pythagoras here to get the label positions to show up radially on the cone
    if (component.type === GeckoComponentModelType.LegacyCone || !show3D) {
      const scalingFactor = 0.95 //Random number to get the labels to look nice
      const scaledCourseMidpoint =
        (model.geometry.end.y.num + model.geometry.start.y.num) /
        (2 * component.outerRadius.num)
      const xyPositon =
        Number(sqrt(scaledCourseMidpoint ** 2 / 2)) * scalingFactor

      return [xyPositon, -xyPositon, 0]
    }
    const coneHeight = component.coneHeight.num
    const radiusDelta = component.outerRadius.num - component.innerRadius.num
    const courseYPosition =
      (model.geometry.start.y.num + model.geometry.end.y.num) / 2
    const relY = component.outerRadius.num - courseYPosition
    const height = (relY / radiusDelta) * coneHeight
    return [courseYPosition * 1.1, height, 0]
  }, [component, model, show3D])

  const { formatMeasurement } = useGeckoLocale()

  const showThisFeatureLabel = React.useMemo(
    () =>
      typeof showFeatureLabels === 'boolean'
        ? showFeatureLabels
        : showFeatureLabels({ feature: model, componentModel: component }),
    [model, component, showFeatureLabels]
  )

  const showThisFeature = React.useMemo(
    () =>
      typeof showFeatures === 'boolean'
        ? showFeatures
        : showFeatures({ feature: model, componentModel: component }),
    [model, component, showFeatures]
  )

  return !!clipConfig.displayedSection &&
    clipConfig.displayedSection !== model.name ? null : (
    <group>
      <TankDecorGroup3D yPivot={show3D}>
        {!nominal ? null : (
          <HtmlOverlay3D
            key={`${model.geometry.componentID}_course_${model.id}`}
            anchor='left'
            x={namePlatePosition[0]}
            y={namePlatePosition[1]}
            z={namePlatePosition[2]}
            priority={HtmlOverlayPriority.MajorFeatureNamePlate}
            visible={showThisFeatureLabel && showThisFeature}>
            <div
              style={{
                paddingLeft: '0.5rem',
                lineHeight: 1,
              }}>
              <h4>{name}</h4>
              <small>
                Nominal: {formatMeasurement({ measurement: nominal })}
              </small>
              <br></br>
              <small>
                Course End:{' '}
                {formatMeasurement({
                  measurement: y_end,
                  variant: 'geometric',
                })}
              </small>
            </div>
          </HtmlOverlay3D>
        )}
      </TankDecorGroup3D>

      <SurfaceMeshFeature3D
        path={path}
        featureModel={model}
        showDebugColors={showShellCourseDebugColors}
        selectionType={SurfaceMeshSelectionType.VizToolTipSelectable}
        userDataName={'course'}
      />
    </group>
  )
}
